import React from 'react';
import type { TCascadeFilterOption, IRadioOptionItem } from './_type';
import type { OptionsItemType } from '@/pages/home/components/NewRegionSelect/type';
import type { IFilterOptionDisplayOrderConfig } from '@/pages/dashboard/components/NewFilter/_type';
import {
  formatRegionOptions,
  formatCascadeOptions,
  getRegionSelectedData,
} from '@/pages/dashboard/components/NewFilter/util';
import { ALL_VALUE } from '../../_config/constant';

/**
 * 获取 parentKey 和 childKey
 * @param configParentKey - 配置中的 parentKey
 * @param configChildKey - 配置中的 childKey
 * @param options - 选项数组
 * @param filterKey - 筛选器的 key
 * @returns parentKey 和 childKey 的对象
 */
export function getParentChildKeys(
  configParentKey: string | undefined,
  configChildKey: string | undefined,
  options: TCascadeFilterOption[] | undefined,
  filterKey: string,
): { parentKey: string; childKey: string; } {
  // 如果配置中指定了，使用配置的值（为将来后端支持配置预留）
  if (configParentKey && configChildKey) {
    return { parentKey: configParentKey, childKey: configChildKey };
  }

  // 检查是否是级联筛选器格式（扁平数组，有 type 字段）
  if (
    options &&
    options.length > 0 &&
    options[0]?.type &&
    options[0]?.code !== undefined
  ) {
    // 级联筛选器：parentKey 使用 'parent'，childKey 使用筛选器的 key
    // 值格式：{ parent: [...], [filterKey]: [...], type: 'parent' | filterKey }
    // 确保 filterKey 存在，否则使用默认值
    const finalChildKey = filterKey || 'children';
    return { parentKey: 'parent', childKey: finalChildKey };
  }

  // 默认值（用于地区筛选器等）
  return { parentKey: 'parent', childKey: 'children' };
}

/**
 * 将筛选器系统的值格式转换为 NewRegionSelect 格式
 * @param value - 筛选器系统的值格式
 * @param parentKey - 父级 key
 * @param childKey - 子级 key
 * @returns NewRegionSelect 格式的值
 */
export function convertToRegionSelectValue(
  value: { [key: string]: string[] | string; type: string; } | null | undefined,
  parentKey: string,
  childKey: string,
): { regions: string[]; markets: string[]; region_type: string; } | null {
  if (!value) return null;

  // 处理级联筛选器的值格式
  // 值可能是 { type: [...], code: [...], type: 'type' | 'code' }
  // 或者 { parent: [...], children: [...], type: 'parent' | 'children' }
  const parentValues = value[parentKey] || [];
  const childValues = value[childKey] || [];

  // NewRegionSelect 期望的格式：{ type: 'region' | 'market', regions: [...], markets: [...] }
  // 注意：这里的 type 是 'region' 或 'market'，不是 parentKey 或 childKey
  return {
    regions: Array.isArray(parentValues) ? parentValues : [],
    markets: Array.isArray(childValues) ? childValues : [],
    region_type: value.type === parentKey ? 'region' : 'market',
  };
}

/**
 * 将选项转换为 NewRegionSelect 格式
 * @param options - 原始选项数组
 * @param displayOrder - 可选；传入时交给 formatCascadeOptions → applyFilterOptionDisplayOrder 做置顶与排序
 * @returns 格式化后的选项数组
 */
export function convertToRegionSelectOptions(
  options: TCascadeFilterOption[] | undefined,
  displayOrder?: IFilterOptionDisplayOrderConfig,
): OptionsItemType[] {
  if (!options || options.length === 0) return [];

  let result: OptionsItemType[];

  // 如果选项已经是格式化后的格式（有 children 属性），直接使用
  if (options[0]?.children || options[0]?.country) {
    result = options.map((item: TCascadeFilterOption) => ({
      ...item,
      text: item.text || (item as any).region_name_en || (item as any).name_en,
      textCN:
        item.textCN || (item as any).region_name_cn || (item as any).name_ch,
      value: item.value || (item as any).region_abbre || (item as any).code,
      children: item.children || item.country || [],
    })) as OptionsItemType[];
  } else if (options[0]?.type && options[0]?.code !== undefined) {
    // 检查是否是级联筛选器格式（扁平数组，有 type 字段）
    // 使用 formatCascadeOptions 格式化级联筛选器选项；displayOrder 控制各 type 分组内子项顺序（置顶 + 拼音序）
    result = formatCascadeOptions(
      options,
      'type',
      'code',
      'name_ch',
      'name_en',
      displayOrder,
    ) as OptionsItemType[];
  } else {
    // 否则使用 formatRegionOptions 格式化（地区筛选器）
    result = formatRegionOptions(options as any) as unknown as OptionsItemType[];
  }

  return result;
}

/**
 * 计算最终的值（使用 getRegionSelectedData 处理）
 * @param regionSelectOptions - 格式化后的选项数组
 * @param regionSelectValue - 格式化后的值
 * @returns 最终的值
 */
export function calculateResultValue(
  regionSelectOptions: OptionsItemType[],
  regionSelectValue: {
    regions: string[];
    markets: string[];
    region_type: string;
  } | null,
): { regions: string[]; markets: string[]; type: string; } {
  if (!regionSelectValue || !regionSelectOptions.length) {
    // 如果没有值，返回一个空值结构，确保组件能正常工作
    return {
      regions: [],
      markets: [],
      type: 'market',
    };
  }
  return getRegionSelectedData(regionSelectOptions, regionSelectValue);
}

/**
 * 获取语言配置
 * @param name - 名称
 * @param lftName - 左侧名称
 * @param rtName - 右侧名称
 * @param parentChildName - 父级/子级组合名称
 * @returns 语言配置对象
 */
export function getLanguageConfig(
  name: string | undefined,
  lftName: string | undefined,
  rtName: string | undefined,
  parentChildName: string | undefined,
): {
    en: { button_region: string; button_market: string; region_title: string; };
    cn: { button_region: string; button_market: string; region_title: string; };
  } {
  // 当没有 parentChildName 时，button_market 回退到使用筛选器名称，确保在没有选中值时也能显示名称
  const displayName = name || rtName || '';
  const marketName = parentChildName || displayName;
  return {
    en: {
      button_region: displayName,
      button_market: marketName,
      region_title: lftName || '',
    },
    cn: {
      button_region: displayName,
      button_market: marketName,
      region_title: lftName || '',
    },
  };
}

/**
 * 格式化单选选项
 * @param radioOptions - 原始单选选项数组
 * @param allInfo - 全部选项的提示信息
 * @param name - 名称
 * @param getLocalText - 国际化函数
 * @param createInfoIcon - 创建信息图标的函数
 * @returns 格式化后的单选选项数组
 */
export function formatRadioOptions(
  radioOptions: IRadioOptionItem[] | undefined,
  allInfo: string | undefined,
  name: string | undefined,
  getLocalText:
  | ((
    key: string,
    params?: Record<string, unknown>,
    fallback?: boolean,
  ) => string)
  | undefined,
  createInfoIcon: (
    infoText: string | null | undefined,
  ) => React.ReactElement | null,
): OptionsItemType[] {
  if (!radioOptions || radioOptions.length === 0) {
    const defaultInfo =
      allInfo ||
      getLocalText?.(
        'selector_multiple_component_common_all_info',
        { name },
        false,
      );
    return [
      {
        value: ALL_VALUE,
        text:
          getLocalText?.(
            'selector_multiple_component_custom_filter_all_describe',
            {},
            false,
          ) || 'All',
        textCN:
          getLocalText?.(
            'selector_multiple_component_custom_filter_all_describe',
            {},
            false,
          ) || '全部',
        infoIcon: createInfoIcon(defaultInfo),
      },
    ] as OptionsItemType[];
  }
  return radioOptions.map((item: IRadioOptionItem) => {
    // 优先使用 item.info，否则使用 allInfo，最后使用默认提示文本
    const itemInfo =
      item.info ||
      allInfo ||
      getLocalText?.(
        'selector_multiple_component_common_all_info',
        { name },
        false,
      );
    return {
      value: item.value,
      text: item.text,
      textCN: item.textCN,
      render: item.render,
      disabled: item.disabled,
      // 如果已有 infoIcon，使用它；否则将 info 转换为 infoIcon
      infoIcon: item.infoIcon || createInfoIcon(itemInfo),
    };
  }) as OptionsItemType[];
}

/**
 * 计算 realRegionsList（NewRegionSelect 需要的 prop）
 * @param regionSelectOptions - 格式化后的选项数组
 * @returns 地区代码数组
 */
export function calculateRealRegionsList(
  regionSelectOptions: OptionsItemType[],
): string[] {
  return regionSelectOptions
    .map(
      (item: OptionsItemType) =>
        item.value || (item as { region_abbre?: string; }).region_abbre,
    )
    .filter(Boolean) as string[];
}

/**
 * 将 NewRegionSelect 的值格式转换回筛选器系统格式
 * @param newValue - NewRegionSelect 返回的值
 * @param filterKey - 筛选器的 key
 * @param parentKey - 父级 key
 * @param childKey - 子级 key
 * @returns 筛选器系统的值格式
 */
export function convertFromRegionSelectValue(
  newValue: {
    type?: string;
    region_type?: string;
    regions?: string[];
    markets?: string[];
  } | null,
  filterKey: string,
  parentKey: string,
  childKey: string,
): { [key: string]: string[] | string; type: string; } | null {
  if (!newValue) {
    // 如果值为空，设置为空对象
    return {
      [parentKey]: [],
      [childKey]: [],
      type: childKey,
    };
  }

  // NewRegionSelect 返回的值格式：{ type: 'region' | 'market', regions: [...], markets: [...] }
  // 注意：这里的 type 是 'region' 或 'market'，不是 parentKey 或 childKey
  const regions = newValue.regions || [];
  const markets = newValue.markets || [];
  const regionType = newValue.region_type || newValue.type || 'market';

  // 确保 childKey 有值，如果没有则使用 filterKey
  const finalChildKey = childKey || filterKey || 'children';
  const finalParentKey = parentKey || 'parent';

  return {
    [finalParentKey]: regions,
    [finalChildKey]: markets,
    type: regionType === 'region' ? finalParentKey : finalChildKey,
  };
}
